import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import i18next from 'i18next';
import clsx from 'clsx';

import { Button } from 'primereact/button';

import PaymentDetails from '../../payments/paymentDetails';

const t = (i18nKey, params) => i18next.t(`userDashboard.billing.add_credits.${i18nKey}`, params);

const AddCredits = () => {
    const location = useLocation();

    const [credits, setCredits] = useState(50); // Default 50 Credits
    const [currentPrice, setCurrentPrice] = useState(1990); // Default 50 Credits

    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(0);
    const [columnsWidth, setColumnsWidth] = useState(0);
    const [displayCredits, setDisplayCredits] = useState(credits);

    const initialLoad = useRef(true);
    const columnsRef = useRef(null);
    const intervalRef = useRef(null);

    const handleDisplayModal = () => {
        if (isLoading) return;
        setShowModal(!showModal);
    };

    useLayoutEffect(() => {
        if (paymentStatus) return;

        const paymentStatusParam = new URLSearchParams(location.search).get('payment_status');

        if (paymentStatusParam && initialLoad.current) {
            if (paymentStatusParam === '1') {
                setPaymentStatus(1);
            } else if (paymentStatusParam === '2') {
                setPaymentStatus(2);
                return;
            }
        }

        initialLoad.current = false;
    }, [location.search, paymentStatus]);

    useLayoutEffect(() => {
        const handleResize = (entries) => {
            for (let entry of entries) {
                const width = entry.contentRect.width;
                if (width > 0) {
                    setColumnsWidth(width);
                }
            }
        };

        const observer = new ResizeObserver(handleResize);
        if (columnsRef.current) {
            const initialWidth = columnsRef.current.getBoundingClientRect().width;
            if (initialWidth > 0) {
                setColumnsWidth(initialWidth);
            }
            observer.observe(columnsRef.current);
        }

        return () => {
            if (columnsRef.current) {
                observer.unobserve(columnsRef.current);
            }
        };
    }, []);

    const isSmallColumns = columnsWidth <= 768;

    const handleCreditsChange = (newCredits, newPrice) => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        const increment = newCredits > credits ? 1 : -1;
        intervalRef.current = setInterval(() => {
            setDisplayCredits((prev) => {
                if (prev === newCredits) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    return prev;
                }
                return prev + increment;
            });
        }, 10);
        setCredits(newCredits);
        setCurrentPrice(newPrice);
    };

    return (
        <div className='pack-prices'>
            <div className="mb-5">
                <div className="has-text-left has-text-grey-light has-text-weight-bold is-size-4 mb-1">
                    {t('nos_packs')}
                </div>
                <div className="has-text-left grille-tarifaire has-text-weight-bold is-size-1 mb-4">
                    {t('grille_tarifaire')}
                </div>

                <div ref={columnsRef}>
                    <div className={clsx('columns is-multiline is-variable is-2')} style={!isSmallColumns ? { padding: '0 15vw' } : {}}>
                        {[
                            { volume: t('add_credits', { credits: 10 }), intVolume: 10, price: '470€ HT', intPrice: 470, tag: '', tagGradient: '' },
                            { volume: t('add_credits', { credits: 25 }), intVolume: 25, price: '1150 HT', intPrice: 1150, tag: '', tagGradient: '' },
                            { volume: t('add_credits', { credits: 50 }), intVolume: 50, price: '1 990 HT', intPrice: 1990, tag: 'Populaire', tagGradient: 'linear-gradient(80deg, #ED587A 0%, #ED704C 100%)' },
                            { volume: t('add_credits', { credits: 75 }), intVolume: 75, price: '2 900€ HT', intPrice: 2900, tag: '', tagGradient: '' },
                            { volume: t('add_credits', { credits: 100 }), intVolume: 100, price: '3 750€ HT', intPrice: 3750, tag: '', tagGradient: '' },
                        ].map((item, index) => (
                            <div key={index} className="column is-full">
                                <div
                                    className={clsx('box pack-price-box', { 'selected': credits === item.intVolume, 'mx-6': !isSmallColumns })}
                                    onClick={() => handleCreditsChange(item.intVolume, item.intPrice)}
                                >
                                    {item.tag && (
                                        <div className="tag top-right-tag" style={{ background: item.tagGradient }}>
                                            {item.tag}
                                        </div>
                                    )}
                                    <div className="columns is-mobile">
                                        <div className={clsx('column has-text-left pr-0 my-2')}>
                                            <span className="tag volume-tag">{item.volume}</span>
                                        </div>
                                        {item.price &&
                                            <div className="column has-text-right pl-1 my-2">
                                                <span className="tag price-tag">{item.price}</span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="column is-full">
                            <div className="columns is-mobile">
                                <div className="column has-text-centered">
                                    <span className="tag contact-tag p-5">{t('besoin_de_plus_options')} <a href="/contact" target='_blank' className='ml-2' style={{ color: 'white', textDecoration: 'underline' }}>{t('nous_contacter')}</a>
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div className='has-text-centered'>
                <Button className={clsx('button is-info is-medium radius-5', { 'is-loading': isLoading })} onClick={handleDisplayModal}>
                    {t('add_credits', { credits: displayCredits })}
                </Button>
            </div>

            <PaymentDetails
                visible={showModal}
                onHide={() => {
                    setShowModal(false);
                    setPaymentStatus(0);
                }}
                credits={credits}
                discountAmount={null}
                oldTotalCost={currentPrice}
                newTotalCost={currentPrice}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                paymentStatus={paymentStatus}
            />

            <hr className='is-invisible' />
        </div>
    );
};

export default AddCredits;